import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import getAllOrganizations from 'redux/actions/Common/getAllOrganizations';

const ORG_PAGE_SIZE = 20;

// Debounced server-side org search on top of `/ads/org/search`, with "load
// more" pagination (the cursor is the id of the last org on the page).
const useOrgSearch = ({ enabled = true, debounceMs = 400 } = {}) => {
  const dispatch = useDispatch();
  const [keyword, setKeyword] = useState('');
  const [organizations, setOrganizations] = useState([]);
  const [fetching, setFetching] = useState(false);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState(null);
  // Drops responses from searches that were superseded while in flight.
  const requestSeq = useRef(0);

  const search = (kw, lastSeen) => {
    const seq = ++requestSeq.current;
    const trimmed = kw?.trim();
    if (lastSeen) setFetchingMore(true);
    else setFetching(true);
    setError(null);
    dispatch(
      getAllOrganizations(
        trimmed ? [trimmed] : [],
        false,
        'n_org',
        ORG_PAGE_SIZE,
        lastSeen || null,
        'v2',
        (data) => {
          if (seq !== requestSeq.current) return;
          const page = data || [];
          setOrganizations((existing) => {
            if (!lastSeen) return page;
            const seen = new Set(existing.map((org) => org?.id));
            return [...existing, ...page.filter((org) => !seen.has(org?.id))];
          });
          setHasMore(page.length === ORG_PAGE_SIZE);
          setFetching(false);
          setFetchingMore(false);
        },
        (message) => {
          if (seq !== requestSeq.current) return;
          setError(message);
          setFetching(false);
          setFetchingMore(false);
        },
      ),
    );
  };

  useEffect(() => {
    if (!enabled) return undefined;
    const timer = setTimeout(() => search(keyword), debounceMs);
    return () => clearTimeout(timer);
  }, [keyword, enabled]);

  const loadMore = () => {
    const lastSeen = organizations?.[organizations.length - 1]?.id;
    if (lastSeen) search(keyword, lastSeen);
  };

  return {
    keyword,
    setKeyword,
    organizations,
    fetching,
    fetchingMore,
    hasMore,
    error,
    loadMore,
  };
};

export default useOrgSearch;
